import { Grid } from '@mui/material';
import { ODSLoadingSpinner, ODSTagStatic, ODSText } from '@telekom-ods/react-ui-kit';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';

import Caas from '../../../../../api/caas';
import Alert from '../../../../../components/Alert';
import Button from '../../../../../components/Buttons/Button';
import { Checkbox } from '../../../../../components/InputFields/Checkbox';
import { ODSSelect } from '../../../../../components/InputFields/ODSSelect';
import Modal from '../../../../../components/Modal';
import useApiQuery from '../../../../../hooks/useApiQuery';
import useAppContext from '../../../../../hooks/useAppContext';

import ClusterInfo from './ClusterInfo';
import './UpgradeCluster.css';
import UpgradeProgress from './UpgradeProgress';
import useClusterUpgrade from './useClusterUpgrade';
import ValidationResults from './ValidationResults';

import type { ClusterDetailsResponse, UpgradeClusterFormProps } from '@/types/caasTypes';

const UpgradeClusterForm = ({
  clusterName,
  clusterType,
  cloud,
  owner,
  onCancel,
}: UpgradeClusterFormProps): React.JSX.Element | null => {
  const intl = useIntl();
  const { theme } = useAppContext();
  const [confirmed, setConfirmed] = useState(false);

  const {
    makeApiCall: fetchClusterDetails,
    data: clusterDetails = null,
    isLoading: isDetailsLoading,
    error: detailsError,
  } = useApiQuery<ClusterDetailsResponse>(
    Caas.fetchClusterDetails as unknown as (args: unknown) => Promise<ClusterDetailsResponse>,
    { disableLoader: true }
  );

  useEffect(() => {
    fetchClusterDetails(clusterName);
  }, [clusterName, fetchClusterDetails]);

  const {
    statusItem,
    statusError,
    upgradeEligibility,
    isValidationLoading,
    validationError,
    isLive,
    connectionLost,
    upgradeRequested,
    selectedVersion,
    setSelectedVersion,
    isUpgradeInProgress,
    isAwaitingPostCheck,
    canRunValidationCheck,
    isEligible,
    canUpgrade,
    isFullyComplete,
    newUpgradeStarted,
    startNewUpgrade,
    handleUpgrade,
    refetchStatus,
    revalidate,
  } = useClusterUpgrade(clusterName);

  const wasUpgrading = useRef(false);

  useEffect(() => {
    if (wasUpgrading.current && !isUpgradeInProgress) {
      fetchClusterDetails(clusterName);
    }
    wasUpgrading.current = isUpgradeInProgress;
  }, [isUpgradeInProgress, clusterName, fetchClusterDetails]);

  const currentClusterVersion = clusterDetails?.cluster_version ?? '';

  const versionOptions = clusterDetails
    ? [
        { key: 'latest_patch_version', value: clusterDetails.latest_patch_version },
        { key: 'latest_minor_version', value: clusterDetails.latest_minor_version },
        { key: 'next_latest_version', value: clusterDetails.next_latest_version },
        { key: 'latest_major_version', value: clusterDetails.latest_major_version },
      ].filter(opt => opt.value !== currentClusterVersion)
    : [];

  const isUpgradeDisabled = !canUpgrade || !confirmed;

  const postCheckValue = statusItem?.status?.postCheck;
  const postCheckFailed = postCheckValue === 'failed' || postCheckValue === 'error';
  const upgrading =
    isUpgradeInProgress || isAwaitingPostCheck || (upgradeRequested && !isFullyComplete);
  const showCompletionState = isFullyComplete && !newUpgradeStarted;
  // Cluster info and validation stay on-screen through the pre-upgrade and both
  // completion states; only the version picker is hidden once an upgrade can no
  // longer be started (in flight, or a settled completion awaiting the user).
  const canStartUpgrade = !upgrading && !showCompletionState;
  const controlsDisabled =
    !isEligible ||
    isUpgradeInProgress ||
    isAwaitingPostCheck ||
    upgradeRequested ||
    isValidationLoading;

  const upgradeLabel = selectedVersion
    ? intl.formatMessage({ id: 'upgradeToVersion' }, { version: selectedVersion })
    : intl.formatMessage({ id: 'upgrade' });

  // Support is available throughout; only an in-flight upgrade swaps it for the
  // "safe to close, you'll be notified" hint.
  let supportContent: React.ReactNode;
  if (upgrading) {
    supportContent = (
      <>
        {intl.formatMessage({ id: 'upgradeInProgressSupportTextBeforeLink' })}{' '}
        <Link
          to='/account/notifications'
          className='upgrade-support-link'
        >
          {intl.formatMessage({ id: 'notificationLinkText' })}
        </Link>{' '}
        {intl.formatMessage({ id: 'upgradeInProgressSupportTextAfterLink' })}
      </>
    );
  } else {
    supportContent = (
      <>
        {intl.formatMessage({ id: 'needHelp' })}{' '}
        <Link
          to='/support-and-feedback'
          onClick={onCancel}
          className='upgrade-support-link'
        >
          {intl.formatMessage({ id: 'contactSupport' })}
        </Link>
      </>
    );
  }

  let actionButtons: React.ReactNode;
  if (upgrading) {
    actionButtons = (
      <Button
        variant='outline'
        onClick={onCancel}
        label={intl.formatMessage({ id: 'close' })}
      />
    );
  } else if (showCompletionState) {
    actionButtons = (
      <>
        <Button
          variant='outline'
          onClick={onCancel}
          label={intl.formatMessage({ id: 'close' })}
        />
        <Button
          variant='primary'
          onClick={() => {
            setConfirmed(false);
            startNewUpgrade();
          }}
          label={intl.formatMessage({ id: postCheckFailed ? 'tryAgain' : 'newUpgrade' })}
        />
      </>
    );
  } else {
    actionButtons = (
      <>
        <Button
          variant='outline'
          onClick={onCancel}
          label={intl.formatMessage({ id: 'cancel' })}
        />
        <Button
          variant='primary'
          disabled={isUpgradeDisabled}
          onClick={() => {
            handleUpgrade();
          }}
          label={upgradeLabel}
        />
      </>
    );
  }

  return (
    <Modal
      title={intl.formatMessage({ id: 'upgradeClusterModalTitle' }, { name: clusterName })}
      onClose={onCancel}
      hideActions
      width='700'
    >
      <div
        className='upgrade-modal'
        data-theme={theme}
      >
        <Grid
          container
          direction='column'
          gap={2.5}
        >
          <Grid
            container
            alignItems='center'
            gap={1}
            sx={{ width: 'fit-content' }}
          >
            <ODSTagStatic
              label={clusterType.toUpperCase()}
              type='strong'
            />
            {cloud && (
              <ODSText
                as='span'
                className='upgrade-modal-subtitle'
              >
                {cloud}
              </ODSText>
            )}
          </Grid>

          {!upgrading && (
            <Grid
              container
              direction='column'
              gap={1.5}
            >
              {detailsError && (
                <Alert
                  mode='error'
                  title={intl.formatMessage({ id: 'failedToLoadClusterDetails' })}
                  text={detailsError.message}
                />
              )}
              {!detailsError && isDetailsLoading && !clusterDetails && (
                <Grid
                  container
                  alignItems='center'
                  gap={1}
                >
                  <ODSLoadingSpinner size='small' />
                  <ODSText
                    as='p'
                    className='p-text upgrade-text-muted'
                  >
                    {intl.formatMessage({ id: 'loadingClusterDetails' })}
                  </ODSText>
                </Grid>
              )}
              {!detailsError && clusterDetails && (
                <ClusterInfo
                  details={clusterDetails}
                  owner={owner}
                  collapsible
                />
              )}
            </Grid>
          )}

          {statusError && !statusItem && (
            <Grid
              container
              direction='column'
              gap={1.5}
              className='upgrade-section-card'
            >
              <ODSText
                as='p'
                className='upgrade-section-title'
              >
                {intl.formatMessage({ id: 'upgradeStatusTitle' })}
              </ODSText>
              <Alert
                mode='error'
                title={intl.formatMessage({ id: 'failedToLoadUpgradeStatus' })}
                text={statusError.message}
              />
            </Grid>
          )}

          {(!!statusItem || upgradeRequested) && (
            <UpgradeProgress
              statusItem={statusItem}
              isLive={isLive}
              connectionLost={connectionLost}
              upgradeRequested={upgradeRequested}
              onRefresh={refetchStatus}
            />
          )}

          {!upgrading && (
            <ValidationResults
              eligibility={upgradeEligibility}
              isLoading={isValidationLoading}
              error={validationError}
              canRunValidationCheck={canRunValidationCheck}
              onRevalidate={revalidate}
            />
          )}

          {canStartUpgrade && (
            <Grid
              container
              direction='column'
              gap={1.5}
              className='upgrade-section-card upgrade-action'
            >
              <ODSText
                as='p'
                className='upgrade-section-title'
              >
                {intl.formatMessage({ id: 'selectVersionToUpgrade' })}
              </ODSText>
              <ODSSelect
                name='version'
                label={intl.formatMessage({ id: 'selectAVersion' })}
                value={selectedVersion}
                onChange={selected => {
                  const item = selected as { value?: string };
                  setSelectedVersion(item.value ?? '');
                }}
                disabled={controlsDisabled}
                menuItems={versionOptions.map(opt => ({
                  value: opt.value,
                  label: `${opt.value} (${opt.key.replace(/_/g, ' ').replace('_version', '')})`,
                  disabled: opt.key === 'latest_major_version',
                }))}
                style={{ width: '100%', maxWidth: '100%', marginBottom: 0 }}
              />

              {!isEligible && upgradeEligibility?.eligibleForUpgrade !== false && (
                <ODSText
                  as='p'
                  className='p-text upgrade-text-muted'
                >
                  {intl.formatMessage({ id: 'unlocksAfterValidation' })}
                </ODSText>
              )}

              {upgradeEligibility?.eligibleForUpgrade === false && (
                <Alert
                  mode='error'
                  title={
                    upgradeEligibility.message ??
                    intl.formatMessage({ id: 'clusterNotEligibleForUpgrade' })
                  }
                />
              )}

              <Grid
                container
                alignItems='center'
                className={`upgrade-confirm-box${controlsDisabled ? ' upgrade-locked' : ''}`}
              >
                <Checkbox
                  id='confirm-upgrade'
                  name='confirm-upgrade'
                  checked={confirmed}
                  disabled={controlsDisabled}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setConfirmed(e.target.checked);
                  }}
                  label={intl.formatMessage({ id: 'upgradeConfirmationCheckbox' })}
                />
              </Grid>
            </Grid>
          )}

          <Grid
            container
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <ODSText
              as='p'
              className='p-text upgrade-support-text'
            >
              {supportContent}
            </ODSText>
            <Grid
              container
              justifyContent='flex-end'
              gap={1}
              sx={{ width: 'fit-content' }}
            >
              {actionButtons}
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};

export default UpgradeClusterForm;
